/**
 * Robust SSE Parser following W3C EventSource specification
 *
 * Correctly handles:
 * - Multi-line data fields (accumulated with \n)
 * - Event separation by empty lines
 * - Comments (lines starting with :)
 * - Field parsing with optional leading space after colon
 * - Partial chunks and buffering
 *
 * Spec: https://html.spec.whatwg.org/multipage/server-sent-events.html
 */

export interface SSEEvent {
  id?: string
  event?: string
  data: string
}

export class SSEParser {
  private buffer = ''
  private currentEvent: Partial<SSEEvent> = {}

  /**
   * Parse incoming SSE chunk
   * @param chunk Raw text chunk from stream
   * @returns Array of complete events (empty if no complete events yet)
   */
  parse(chunk: string): SSEEvent[] {
    this.buffer += chunk
    const events: SSEEvent[] = []

    const lines = this.buffer.split('\n')

    // Keep last line in buffer (might be incomplete)
    this.buffer = lines.pop() || ''

    for (const line of lines) {
      // Empty line = event boundary (dispatch event)
      if (line.trim() === '') {
        if (this.currentEvent.data !== undefined) {
          // Remove trailing newline from accumulated data
          const data = this.currentEvent.data.replace(/\n$/, '')

          // Build event object with only defined fields (exactOptionalPropertyTypes)
          const event: SSEEvent = { data }
          if (this.currentEvent.id !== undefined) {
            event.id = this.currentEvent.id
          }
          if (this.currentEvent.event !== undefined) {
            event.event = this.currentEvent.event
          }

          events.push(event)

          // Reset for next event
          this.currentEvent = {}
        }
        continue
      }

      // Comment line (ignore)
      if (line.startsWith(':')) {
        continue
      }

      // Parse field: value
      const colonIndex = line.indexOf(':')
      if (colonIndex === -1) {
        // Malformed line without colon - ignore per spec
        continue
      }

      const field = line.slice(0, colonIndex)
      let value = line.slice(colonIndex + 1)

      // Remove optional leading space after colon (per spec)
      if (value.startsWith(' ')) {
        value = value.slice(1)
      }

      // Process field
      switch (field) {
        case 'data':
          // Accumulate data (can be multi-line)
          this.currentEvent.data = (this.currentEvent.data || '') + value + '\n'
          break

        case 'id':
          this.currentEvent.id = value
          break

        case 'event':
          this.currentEvent.event = value
          break

        case 'retry':
          // Retry field - not used in our implementation
          break

        default:
          // Unknown field - ignore per spec
          break
      }
    }

    return events
  }

  /**
   * Flush any remaining event in buffer
   * Call this when stream ends to get the last event
   */
  flush(): SSEEvent | null {
    if (this.currentEvent.data !== undefined) {
      const data = this.currentEvent.data.replace(/\n$/, '')

      // Build event object with only defined fields (exactOptionalPropertyTypes)
      const event: SSEEvent = { data }
      if (this.currentEvent.id !== undefined) {
        event.id = this.currentEvent.id
      }
      if (this.currentEvent.event !== undefined) {
        event.event = this.currentEvent.event
      }

      this.reset()
      return event
    }
    return null
  }

  /**
   * Reset parser state
   */
  reset(): void {
    this.buffer = ''
    this.currentEvent = {}
  }
}
